import type { CreateElplanBody, UpdateElplanBody } from 'api/elplan/elplan.api'
import Datetime from 'utils/datetime/datetime'
import { getDayQuarterStarts } from 'utils/datetime/dayTimeSteps'
import type { ElplanDraft, ElplanSaveTarget } from './ElplanEditor.types'
import { readElplanVolume } from './volume'

type ElplanCreateTarget = Extract<ElplanSaveTarget, { kind: 'create' }>
type ElplanUpdateTarget = Extract<ElplanSaveTarget, { kind: 'update' }>

export function serializeElplanDraft(draft: ElplanDraft, target: ElplanCreateTarget): CreateElplanBody
export function serializeElplanDraft(draft: ElplanDraft, target: ElplanUpdateTarget): UpdateElplanBody
export function serializeElplanDraft(
  draft: ElplanDraft,
  target: ElplanSaveTarget
): CreateElplanBody | UpdateElplanBody {
  return target.kind === 'create' ? serializeCreate(draft, target) : serializeUpdate(draft, target)
}

function serializeCreate(draft: ElplanDraft, target: ElplanCreateTarget): CreateElplanBody {
  const dayQuarterStarts = getDayQuarterStarts(target.day)
  const holdsEveryQuarterOnce =
    draft.resolution === 'quarter' &&
    draft.slotStarts.length === dayQuarterStarts.length &&
    draft.slotStarts.every((start, index) => start === dayQuarterStarts[index])
  if (!holdsEveryQuarterOnce) {
    throw new Error(`An elplan create needs every quarter of ${target.day} exactly once, in order`)
  }

  return {
    elplan_info: target.elplanInfoId,
    date: Datetime.toLocalTime(target.day, 'onlyDate'),
    values: draft.slotStarts.map((time) => ({ time, volume: readSlotVolumeMw(draft, time) })),
  }
}

function serializeUpdate(draft: ElplanDraft, target: ElplanUpdateTarget): UpdateElplanBody {
  const valueIds = draft.slotStarts.map((start) => target.valueIdByStart[start])
  const holdsOneUniqueIdPerSlot =
    Object.keys(target.valueIdByStart).length === valueIds.length &&
    valueIds.every(Number.isInteger) &&
    new Set(valueIds).size === valueIds.length
  if (!holdsOneUniqueIdPerSlot) {
    throw new Error(`An elplan update needs one unique value id per slot of elplan ${target.elplanId}`)
  }

  return {
    id: target.elplanId,
    values: draft.slotStarts.map((start, index) => ({ id: valueIds[index], volume: readSlotVolumeMw(draft, start) })),
  }
}

function readSlotVolumeMw(draft: ElplanDraft, start: ISODateTime): number {
  const reading = readElplanVolume(draft.textByStart[start] ?? '', 'any')
  if (reading.kind !== 'value') {
    throw new Error(`Elplan slot ${start} holds no volume: ${reading.kind === 'fault' ? reading.fault : 'blank'}`)
  }

  return reading.volumeMw
}
